import { Component, OnDestroy, OnInit, inject, input, signal } from '@angular/core';
import { faFilter } from '@fortawesome/pro-light-svg-icons';
import { Actions, ofType } from '@ngrx/effects';
import { DrawerComponent } from '@progress/kendo-angular-layout';
import { cloneDeep, groupBy } from 'lodash-es';
import { Observable, combineLatest } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { AuthService } from '../../auth/auth.service';
import { TodoEntryModel } from '../../core/models/todo-planner/todo-entry.model';
import { AppConfigService } from '../../core/services/app-config.service';
import * as fromTodoPlannerActions from '../../core/state/todo-planner/todo-planner.actions';
import { DateTimeHelper } from '../calendar/util/date-time-helper';
import { TodoPlannerControlBarService } from './services/todo-planner-control-bar.service';
import { TodoPlannerService } from './services/todo-planner.service';
import { TodoEntryCrudService } from './todo-entry/services/todo-entry-crud.service';
import { TodoEntryService } from './todo-entry/services/todo-entry.service';
import { TodoPlannerViews } from './utils/todo-planner-types';

@Component({
  selector: 'app-todo-planner',
  templateUrl: './todo-planner.component.html',
  styleUrl: './todo-planner.component.scss',
  standalone: false,
})
export class TodoPlannerComponent implements OnInit, OnDestroy {
  // Signals and Inputs
  readonly plannerViewMode = signal<TodoPlannerViews>(TodoPlannerViews.WeekView);
  readonly drawer = input.required<DrawerComponent>();
  readonly showPopup = signal<boolean>(false);

  // Constants
  public readonly TodoPlannerViews = TodoPlannerViews;
  public readonly faFilter = faFilter;

  // DI
  public readonly todoPlannerControlBarService = inject(TodoPlannerControlBarService);
  public readonly todoPlannerService = inject(TodoPlannerService);
  private readonly appConfigService = inject(AppConfigService);
  private readonly todoEntryService = inject(TodoEntryService);
  private readonly todoEntryCrudService = inject(TodoEntryCrudService);
  private readonly authService = inject(AuthService);
  private readonly actions$ = inject(Actions);

  // Misc
  private readonly subs = new SubSink();
  private user = null;

  ngOnInit(): void {
    combineLatest([
      this.appConfigService.getTodoPlannerConfigData(),
      this.appConfigService.getGlobalAppOptionsData(),
      this.authService.getLoggedInUser(),
    ])
      .pipe(
        take(1),
        switchMap(([todoPlannerConfigModel, globalAppConfigModel, loggedInUser]) => {
          this.user = loggedInUser;
          this.todoPlannerService.buildPlannerModel(
            todoPlannerConfigModel,
            globalAppConfigModel,
            loggedInUser,
          );
          return this.loadTodoPlannerEvents(this.user.id);
        }),
      )
      .subscribe((todoEntries: TodoEntryModel[]) => {
        this.addTodoEntriesToView(todoEntries);
      });

    this.initTodoEntriesReloading();

    this.todoPlannerService.drawer$.next(this.drawer());

    this.initHttpCallbacks();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public togglePopup(): void {
    this.showPopup.update((val) => !val);
  }

  private loadTodoPlannerEvents(userId: number): Observable<TodoEntryModel[]> {
    const todoPlannerModel = this.todoPlannerService.todoPlannerModel;
    let plannerStart = null;
    let plannerEnd = null;

    // Take the previous and the next weeks of the current week to load the necessary planner-events.
    if (todoPlannerModel.plannerViewMode === TodoPlannerViews.WeekView) {
      plannerStart = this.todoPlannerService.todoPlannerModel.plannerStart;
      plannerEnd = this.todoPlannerService.todoPlannerModel.plannerEnd;
    }

    // Take the previous and the next months of the current week to load the necessary planner-events.
    if (todoPlannerModel.plannerViewMode === TodoPlannerViews.MonthView) {
      plannerStart = this.todoPlannerService.todoPlannerModel.plannerStart;
      plannerEnd = this.todoPlannerService.todoPlannerModel.plannerEnd;
    }

    return this.todoPlannerService
      .loadTodoEntries(plannerStart, plannerEnd, userId)
      .pipe(map((arr) => [...arr].sort((a, b) => a.order - b.order)));
  }

  private addTodoEntriesToView(todoEntries: TodoEntryModel[]): void {
    const todoEntryMap = new Map<string, TodoEntryModel[]>(
      Object.entries(groupBy(todoEntries, (entry) => DateTimeHelper.format(entry.start))),
    );

    todoEntryMap.forEach((todoEntriesInContainer) => {
      todoEntriesInContainer.forEach((todoEntryModel, index) => {
        const clonedTodoEntry = TodoEntryService.cloneTodoModel(todoEntryModel);
        clonedTodoEntry.order = index;
        this.todoEntryCrudService.addTodoEntry(clonedTodoEntry, true);
      });
    });

    this.todoPlannerService.calculateMaxParallelIndex();
  }

  private initHttpCallbacks(): void {
    this.subs.sink = this.actions$
      .pipe(
        ofType(
          fromTodoPlannerActions.todoPlannerEntryAdded,
          fromTodoPlannerActions.todoPlannerEntryUpdated,
          fromTodoPlannerActions.todoPlannerEntryDeleted,
        ),
      )
      .subscribe(({ payload, type }) => {
        const alteredTodoEntry = cloneDeep(payload.todoEntryModel) as TodoEntryModel;

        if (type === fromTodoPlannerActions.todoPlannerEntryAdded.type) {
          this.todoEntryCrudService.addTodoEntry(alteredTodoEntry, true);
        } else if (
          // reRenderComponent === false => Don't rerender the component for small changes (e.g. completed prop changed).
          type === fromTodoPlannerActions.todoPlannerEntryUpdated.type &&
          alteredTodoEntry.reRenderComponent
        ) {
          const toBeUpdatedTodoEntry = this.todoEntryCrudService.selectTodoEntry(alteredTodoEntry);
          // Update planner event by removing the old planner component and adding the updated one to the model.
          this.todoEntryCrudService.removeTodoEntry(toBeUpdatedTodoEntry);
          // Update sorted indices of original lane.
          this.todoEntryService.updateSortedIndices(toBeUpdatedTodoEntry.start, alteredTodoEntry);

          this.todoEntryCrudService.addTodoEntry(alteredTodoEntry, true);
          // Update sorted indices of target lane.
          this.todoEntryService.updateSortedIndices(alteredTodoEntry.start, alteredTodoEntry);
        } else if (type === fromTodoPlannerActions.todoPlannerEntryDeleted.type) {
          const toBeDeletedTodoEntry = this.todoEntryCrudService.selectTodoEntry(alteredTodoEntry);
          this.todoEntryCrudService.removeTodoEntry(toBeDeletedTodoEntry);
          this.drawer().toggle();
        }

        this.todoPlannerService.calculateMaxParallelIndex();
        this.todoEntryService.updateSortedIndices(alteredTodoEntry.start, alteredTodoEntry);
      });
  }

  private initTodoEntriesReloading(): void {
    this.subs.sink = this.todoPlannerService.reloadTodoEntries$
      .pipe(
        switchMap(() => {
          this.todoPlannerService.clearAllTodoEntryComponents();
          this.todoPlannerService.setPlannerDays();

          return this.loadTodoPlannerEvents(this.user.id);
        }),
      )
      .subscribe((todoEntries: TodoEntryModel[]) => {
        this.addTodoEntriesToView(todoEntries);
      });
  }
}
